import React, { useState } from 'react';
import QuizGame from '../../components/organisms/QuizGame';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import QUERY_SINGLE_QUIZ from '../../graphql/query/quiz/singleQuiz';
import { LoadingLayout } from 'components/molecules/Loaders/LoadingLayout';
import { ErrorContainer } from 'components/organisms/Error/ErrorContainer';
import { SingleQuiz } from '../../types/quiz/quiz.def';
import { nextQuestion } from 'store/actions/quiz.action';
import { SEND_QUIZ_NOTE } from '../../graphql/mutation/quiz/sendQuizNote';
import FinDeQuiz from './FinDeQuiz';
import { useRefetchData } from '../../helpers/refetch/refetchData';
import useGoToPage from '../../hooks/useGoToPage';
import pages from '../../routes/pages';
import { capture } from '../../services/sentry/capture';
import { SingleQuizQuery } from '../../graphql/generated/graphql';
import useStyle from './quizSingle.style';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/reducers';

interface QuizSingleProps {
  id: string;
  match: { [key: string]: { [key: string]: string } };
}

const QuizSingle = ({ match }: QuizSingleProps) => {
  const [load, setLoading] = useState(false);
  const goToNotePage = useGoToPage(pages.NOTE_PAGE.path);
  const goToHome = useGoToPage(pages.HOME_PAGE.path);
  const dispatch = useDispatch();
  const { currentQuestion, reponses } = useSelector(
    (state: AppState) => state.quiz
  );
  const classes = useStyle();
  const { loading, error, data, refetch } = useQuery<SingleQuizQuery>(
    QUERY_SINGLE_QUIZ,
    {
      variables: { id: match.params.id }
    }
  );
  useRefetchData(refetch);
  const [sendNote, { error: mutError }] = useMutation(SEND_QUIZ_NOTE, {
    onCompleted: res => {
      toast.success(
        "Merci d'avoir répondu au quiz " +
          res?.createQuizNote?.quiz?.data?.attributes?.name +
          '. Votre note : ' +
          res?.createQuizNote?.note +
          '/ 20'
      );
      return goToNotePage();
    },
    onError: e => {
      toast.error("Erreur dans l'attribution de la note", {
        icon: '😕'
      });
      capture(e.message, [{ key: 'error', extra: e }]);
    }
  });

  if (loading) return <LoadingLayout />;
  if (error)
    return <ErrorContainer text={error.message} onClick={() => goToHome()} />;

  const quiz: SingleQuiz = data.findOneAvailableQuiz && {
    desc: data?.findOneAvailableQuiz?.desc,
    name: data?.findOneAvailableQuiz?.name,
    questions: data?.findOneAvailableQuiz?.questions?.data?.map(q => ({
      name: q?.attributes?.name,
      reponse1: q?.attributes?.reponse1,
      reponse2: q?.attributes?.reponse2,
      reponse3: q?.attributes?.reponse3,
      id: q?.id
    }))
  };
  const total = quiz?.questions?.length;

  const next = (question, reponse) => {
    setLoading(true);
    setTimeout(() => {
      dispatch(nextQuestion(question, reponse));
      setLoading(false);
    }, 700);
  };
  const last = async () => {
    setLoading(true);
    await sendNote({
      variables: { quizId: match.params.id, quizRep: reponses }
    });
    setLoading(false);
  };

  return (
    <div className={classes.root}>
      <div>
        {quiz?.questions?.map(
          (question, index) =>
            currentQuestion === index + 1 &&
            currentQuestion <= total && (
              <QuizGame
                key={question.id}
                question={question}
                next={next}
                load={load}
                remainingQuestions={
                  'Question ' + currentQuestion + ' sur ' + total
                }
              />
            )
        )}
        {currentQuestion > total && (
          <FinDeQuiz last={last} questions={quiz?.questions} loading={load} />
        )}
        {mutError && (
          <>
            <ErrorContainer
              detailErr={new ApolloError(mutError)}
              text={mutError.message}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default QuizSingle;
